// ============================================================================
// src/lib/pigPlannerApi.ts — transactional Pig planned/actual trip mutations
// ----------------------------------------------------------------------------
// Thin wrappers over the SECURITY DEFINER RPCs from
// supabase-migrations/176_processing_lifecycle_reconcile.sql. These replace the
// former client-side app_store JSON surgery for pig PLANNED-TRIP changes, the
// weigh-in Send-to-Trip fulfillment, undo-send, and actual-trip edit/delete:
// the database locks the ppp-feeders-v1 row + the touched weigh_ins in ONE
// transaction, performs the JSON surgery preserving unrelated fields, and
// re-syncs that group's Processing records — no client dual-writes.
//
// Role model (server-enforced): management/admin only, matching the previous
// client gating on these flows. Deterministic failures carry the
// 'PROCESSING_VALIDATION:' prefix (see processingApi.js error helpers).
//
// The planned-trip LOCK sidecar (ppp-pig-planned-trip-locks-v1) stays
// client-owned in usePigPlannedTrips: a lock is the "scheduled with the
// processor" soft signal, and the RPCs refuse date-edit/move/delete/chain-add
// on locked trips server-side as well.
//
// Every wrapper throws Error(`<fn>: <message>`) on rpc error. After any of
// these mutations the caller must RELOAD feeder groups from app_store (the
// server changed them); fcrCached remains a client-maintained display cache
// recomputed on the trip-edit/delete paths as before.
// ============================================================================

import {applyFeedFenceOperation, freezeFeedFenceRequest, type FeedFenceReceipt} from './feedReceiptOperationApi.js';
import type {RpcEnvelope, SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';

// MIGRATION 237: every trip mutation below goes through ONE audited wrapper,
// pig_trip_operation, which runs the same 176/210 function unchanged and adds
// what those never had -- a request receipt (so a retry after a lost answer
// replays instead of applying twice: a retried "add planned trip" used to add a
// second trip) and its Activity row, in the same transaction. Each call is one
// user intent: ONE request id, reused only to retry a lost answer. The result
// is the minimal projection the wrapper stores (trip_id, trip_date, sent,
// already_attached, detached, changed, moved_count, weigh_ins_released, ...),
// identical on a first call and a replay; callers re-read the feeder groups.
export function newPigRequestId(): string {
  const c = (globalThis as {crypto?: {randomUUID?: () => string}}).crypto;
  return (
    'pig-' +
    (c && typeof c.randomUUID === 'function'
      ? c.randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2))
  );
}

// A lost answer has no SQLSTATE; a database refusal is an answer and is final.
function isTransportFailure(error: SupabaseErrorLike | null | undefined): boolean {
  const code = error && (error as {code?: unknown}).code;
  return !(typeof code === 'string' && code.length > 0);
}

async function tripOperation(
  sb: SupabaseClient,
  label: string,
  operation: string,
  args: Record<string, unknown>,
  requestId: string = newPigRequestId(),
): Promise<RpcEnvelope> {
  let last: SupabaseErrorLike | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const {data, error} = await sb.rpc('pig_trip_operation', {
        p_request_id: requestId,
        p_operation: operation,
        p_args: args,
      });
      if (!error) return data;
      last = error;
      if (!isTransportFailure(error)) break;
    } catch (e) {
      last = e as SupabaseErrorLike;
    }
  }
  throw new Error(`${label}: ${(last && (last as {message?: string}).message) || String(last)}`);
}

// After the existing automatic allocation writer confirms its feeder save,
// synchronize only that stored group's derived Processing records (mig 212).
// Operational roles may call this metadata-only sync; trip edits below retain
// their management/admin gates. The server reads and locks the source group.
export async function pigSyncProcessingTripNumbers(sb: SupabaseClient, groupId: string): Promise<RpcEnvelope> {
  const {data, error} = await sb.rpc('pig_sync_processing_trip_numbers', {p_group_id: groupId});
  if (error) throw new Error(`pigSyncProcessingTripNumbers: ${error.message || String(error)}`);
  return data;
}

// Every argument below crosses the boundary as an RPC parameter the migration
// validates itself. Ids are the text/uuid keys the planner already carries,
// dates are ISO `YYYY-MM-DD` strings, and each option bag is optional in the
// same way the JavaScript defaults made it optional.
export type PigPlannedTripAdd = {
  groupId?: string | null;
  subBatchId?: string | null;
  sex?: string | null;
  date?: string | null;
  plannedCount?: number | null;
};

export async function pigAddPlannedTrip(
  sb: SupabaseClient,
  {groupId, subBatchId, sex, date, plannedCount}: PigPlannedTripAdd = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigAddPlannedTrip', 'trip.add_planned', {
    group_id: groupId,
    sub_batch_id: subBatchId,
    sex,
    date,
    count: plannedCount ?? 0,
  });
}

export async function pigSetPlannedTripDate(
  sb: SupabaseClient,
  {groupId, tripId, date}: {groupId?: string | null; tripId?: string | null; date?: string | null} = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigSetPlannedTripDate', 'trip.set_planned_date', {
    group_id: groupId,
    trip_id: tripId,
    date,
  });
}

export async function pigMovePlannedPigs(
  sb: SupabaseClient,
  {
    groupId,
    fromTripId,
    toTripId,
    count,
  }: {groupId?: string | null; fromTripId?: string | null; toTripId?: string | null; count?: number | null} = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigMovePlannedPigs', 'trip.move_planned', {
    group_id: groupId,
    from_trip_id: fromTripId,
    to_trip_id: toTripId,
    count,
  });
}

export async function pigDeletePlannedTrip(
  sb: SupabaseClient,
  {groupId, tripId}: {groupId?: string | null; tripId?: string | null} = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigDeletePlannedTrip', 'trip.delete_planned', {group_id: groupId, trip_id: tripId});
}

// Fulfillment: sends the given draft weigh-in entries to the processor. The
// server consumes the planned chain (reconcile semantics), PROMOTES the target
// planned trip id into processingTrips unchanged (the Processing record keeps
// its identity), stamps weigh_ins.sent_to_trip_id/sent_to_group_id, and moves
// any under-send remainder to the next planned trip or a NEW planned trip id.
// Returns {ok, trip_id, trip_date, sent, remainder, remainder_trip_id}.
export async function pigSendToTrip(
  sb: SupabaseClient,
  {
    groupId,
    subBatchId,
    sex,
    weighInIds,
  }: {groupId?: string | null; subBatchId?: string | null; sex?: string | null; weighInIds?: string[] | null} = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigSendToTrip', 'trip.send', {
    group_id: groupId,
    sub_batch_id: subBatchId,
    sex,
    weigh_in_ids: Array.isArray(weighInIds) ? weighInIds : [],
  });
}

// Undo one sent entry: clears its stamps, decrements the actual trip, and
// returns the pig to the planned chain. When the last entry is undone the
// actual trip reverts to a PLANNED trip with the SAME id (record identity and
// local work preserved; its Processing record flips back to Planned).
export async function pigUndoSend(sb: SupabaseClient, weighInId: string): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigUndoSend', 'trip.undo_send', {weigh_in_id: weighInId});
}

// The planner-owned facts an actual trip edit may carry. `liveWeights` is the
// per-pig weight list a legacy (weigh-in-less) trip records; a linked trip
// ignores it because the server recomputes from the weigh-ins.
export type PigProcessingTripUpdate = {
  groupId?: string | null;
  tripId?: string | null;
  date?: string | null;
  // `hangingWeight` admits a string because this wrapper performs NO coercion:
  // it forwards the caller's value to the RPC, which validates it server-side.
  // Callers that hold form state coerce first (see usePigProcessingTrips).
  hangingWeight?: number | string | null;
  notes?: string | null;
  pigCount?: number | null;
  // A SPACE/COMMA-SEPARATED STRING, not a list: the planner stores a trip's live
  // weights as the free-form text the operator typed, and src/lib/pig.ts
  // parseLiveWeights is the single parser for it.
  liveWeights?: string | null;
};

// Edit an actual trip's planner-owned facts (date / hangingWeight / notes; for
// legacy trips with no linked weigh-ins also pigCount/liveWeights — linked
// trips recompute those from the weigh-ins server-side).
export async function pigUpdateProcessingTrip(
  sb: SupabaseClient,
  {
    groupId,
    tripId,
    date = null,
    hangingWeight = null,
    notes = null,
    pigCount = null,
    liveWeights = null,
  }: PigProcessingTripUpdate = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigUpdateProcessingTrip', 'trip.update', {
    group_id: groupId,
    trip_id: tripId,
    date: date ?? null,
    hanging_weight: hangingWeight ?? null,
    notes: notes ?? null,
    pig_count: pigCount ?? null,
    live_weights: liveWeights ?? null,
  });
}

// ── Breeding pigs on a trip (migration 210) ─────────────────────────────────
// Attach/detach a breeding pig (sow / gilt / boar) to one ACTUAL processing
// trip. A planned trip is refused server-side: three mig-176 planned-trip
// paths rebuild or remove a trip object and would drop the attachment
// silently, so it is confined to trips that have actually shipped. The server holds the ppp-feeders-v1 row lock for the whole
// operation, so "this animal is not already on another trip" is enforced
// atomically rather than checked hopefully from the client.
//
// Both calls are IDEMPOTENT: attaching an animal already on that same trip
// returns {ok: true, already_attached: true} without a second entry, and
// detaching one that is not attached returns {ok: true, detached: false}.
// Attaching to a DIFFERENT trip is refused with a PROCESSING_VALIDATION error
// naming the trip that holds it.
//
// Neither call touches the breeder's status, archived flag or activity history:
// attachment is MEMBERSHIP, not a status change. Planning a trip therefore
// never marks an animal processed, and detaching never reactivates one.
export type PigTripBreederRef = {
  groupId?: string | null;
  tripId?: string | null;
  breederId?: string | null;
};

export async function pigAttachBreederToTrip(
  sb: SupabaseClient,
  {groupId, tripId, breederId}: PigTripBreederRef = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigAttachBreederToTrip', 'trip.attach_breeder', {
    group_id: groupId,
    trip_id: tripId,
    breeder_id: breederId,
  });
}

export async function pigDetachBreederFromTrip(
  sb: SupabaseClient,
  {groupId, tripId, breederId}: PigTripBreederRef = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigDetachBreederFromTrip', 'trip.detach_breeder', {
    group_id: groupId,
    trip_id: tripId,
    breeder_id: breederId,
  });
}

// Record (or clear, with null) ONE attached breeder's own carcass weight — the
// figure the processor's receipt gives for that animal. It is stored on the
// attachment and NEVER folded into the trip's own hangingWeight: that one is
// the feeder-batch total, matched by the trip's liveWeights, so adding a
// breeder's carcass to it would overstate the batch yield against a live total
// that excludes her. Changes no count, so the server performs no Processing
// re-sync; the animal was already counted when it was attached.
export async function pigSetTripBreederHangingWeight(
  sb: SupabaseClient,
  {groupId, tripId, breederId, hangingWeight = null}: PigTripBreederRef & {hangingWeight?: number | null} = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigSetTripBreederHangingWeight', 'trip.set_breeder_hanging_weight', {
    group_id: groupId,
    trip_id: tripId,
    breeder_id: breederId,
    hanging_weight: hangingWeight,
  });
}

// Delete an actual trip. The server clears dangling weigh-in stamps in the
// same transaction; the trip's Processing record follows the worked-archive vs
// empty-remove rule. REFUSES while a breeding pig is still attached (mig 210).
export async function pigDeleteProcessingTrip(
  sb: SupabaseClient,
  {groupId, tripId}: {groupId?: string | null; tripId?: string | null} = {},
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigDeleteProcessingTrip', 'trip.delete', {group_id: groupId, trip_id: tripId});
}

// ── Migration 237: the three writers that used to upsert ppp-feeders-v1 whole ──

// The planned-trip AUTO-ALLOCATION. The forecast stays in the browser
// (pigForecast.ts); the server applies the computed trips in ONE transaction,
// re-checking under the feeders lock that each (sub-batch, sex) pair still has
// no planned trip -- a pair another tab planned meanwhile is skipped, not
// doubled. Ids and order are the server's. (Not chained through
// pig_add_planned_trip: a second call there DRAWS its count from the first.)
export type PigPlannedTripAllocation = {
  groupId: string;
  subBatchId: string;
  sex: string;
  date: string;
  plannedCount: number;
};
export async function pigAddPlannedTripsBatch(
  sb: SupabaseClient,
  trips: readonly PigPlannedTripAllocation[],
): Promise<RpcEnvelope> {
  return tripOperation(sb, 'pigAddPlannedTripsBatch', 'trip.add_planned_batch', {
    trips: trips.map((t) => ({
      group_id: t.groupId,
      sub_batch_id: t.subBatchId,
      sex: t.sex,
      date: t.date,
      planned_count: t.plannedCount,
    })),
  });
}

async function resolveSessionActor(sb: SupabaseClient): Promise<string> {
  const auth = (
    sb as unknown as {
      auth?: {getSession?: () => Promise<{data?: {session?: {user?: {id?: unknown}} | null}} | null>};
    }
  )?.auth;
  if (!auth || typeof auth.getSession !== 'function') return '';
  try {
    const res = await auth.getSession();
    const id = res?.data?.session?.user?.id;
    return typeof id === 'string' ? id.trim() : '';
  } catch {
    return '';
  }
}

// The admin feed-cost fan-out onto ACTIVE pig batches (the server applies
// today's active filter). Admin only, as the /admin route is. An explicit
// `requestId` lets a caller replay ONE intent by its exact id after an
// unknown outcome (the RPC is 260-receipt-backed); omitted, the operation
// mints its own, exactly as before.
export async function pigApplyFeedRate(
  sb: SupabaseClient,
  rate: number,
  requestId: string = newPigRequestId(),
  fence?: {expectedActor?: string | null; beforeAttempt?: () => Promise<boolean> | boolean},
): Promise<RpcEnvelope & {receipt?: FeedFenceReceipt; notSent?: boolean}> {
  const expectedActor = fence && 'expectedActor' in fence ? (fence.expectedActor ?? '') : await resolveSessionActor(sb);
  const frozen = freezeFeedFenceRequest(
    {
      family: 'pig',
      operation: 'feeder.feed_rate',
      targets: {},
      expected: {},
      change: {rate},
    },
    {
      requestId,
      expectedActor,
    },
  );
  if (!frozen.ok) {
    throw new Error(`pigApplyFeedRate: ${frozen.problem}`);
  }
  const out = await applyFeedFenceOperation(sb, frozen.request, {
    beforeAttempt: fence?.beforeAttempt,
  });
  switch (out.status) {
    case 'settled':
      if (out.receipt.outcome === 'applied' || out.receipt.outcome === 'no_change') {
        return {
          ok: true,
          kind: out.receipt.outcome === 'applied' ? 'updated' : 'no_change',
          request_id: out.receipt.requestId,
          replayed: out.receipt.replayed,
          receipt: out.receipt,
        };
      }
      return {
        ok: false,
        kind: out.receipt.outcome,
        request_id: out.receipt.requestId,
        replayed: out.receipt.replayed,
        receipt: out.receipt,
      };
    case 'not_sent':
      return {ok: false, notSent: true, message: out.problem};
    case 'denied':
    case 'rejected':
      throw new Error(`pigApplyFeedRate: ${out.code}: ${out.message}`);
    case 'unavailable':
    case 'unknown':
      throw new Error(`pigApplyFeedRate: ${out.message}`);
  }
}

// The FCR display cache (fcrCached) one batch carries: a derived value, so no
// receipt and no Activity -- a finite number, or null to clear it.
export async function pigSetFeederFcrCache(
  sb: SupabaseClient,
  groupId: string,
  fcr: number | null,
): Promise<RpcEnvelope> {
  const {data, error} = await sb.rpc('pig_set_feeder_fcr_cache', {p_group_id: groupId, p_fcr: fcr});
  if (error) throw new Error(`pigSetFeederFcrCache: ${error.message || String(error)}`);
  return data;
}
